import { type ProductTaxonomyType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type Ref } from 'vue';

import { useKpisPerimeter, type FiltersTreeType } from '#composables/kpisPerimeter';
import { findBusinessBrands } from '#domain/core/filter/filter.query';
import { filterToTreeViewItemWithDefaultValue } from '../../filter.mapper';

interface UseBusinessBrandDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useBusinessBrandData({
  debouncedSearch,
  enabled,
  checkedItemsState,
  taxonomy
}: UseBusinessBrandDataProps) {
  const {
    filters: { selectedFiltersTree }
  } = useKpisPerimeter();

  const currentBusinessBrandFilters = computed(() => {
    return Object.entries(selectedFiltersTree.value.businessBrandFilters)
      .filter(([, state]) => state.state === 'checked' && state.filterType === 'businessBrand')
      .map(([filter]) => filter);
  });

  const {
    data: businessBrandData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [findBusinessBrands.name, debouncedSearch, currentBusinessBrandFilters, taxonomy],
    queryFn: async () => {
      return findBusinessBrands({
        orNodeIdIn: currentBusinessBrandFilters.value,
        search: debouncedSearch.value,
        taxonomy: unref(taxonomy)
      });
    },
    select: (value) => (value.status === 'success' ? value.data : undefined)
  });

  const itemsBrands = computed(() => {
    if (!businessBrandData?.value) {
      return;
    }

    return filterToTreeViewItemWithDefaultValue({
      filter: businessBrandData.value,
      defaultFilters: businessBrandData.value,
      selectedFilters: checkedItemsState.value.businessBrandFilters
    });
  });

  const isLoadingBrands = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return { itemsBrands, isLoadingBrands };
}
